import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  ArrowLeft, FolderOpen, Download, Upload, Loader2, Pin, Trash2, Search, File,
} from 'lucide-react';
import { toast } from 'sonner';

const ICON_BY_EXT = (name) => {
  const ext = (name || '').split('.').pop().toLowerCase();
  return ext;
};

const bytesFmt = (n) => {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const EmployeeResourcesPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'General', tags: '', is_pinned: false, file: null });

  const canManage = !!user?.is_admin || user?.team_role === 'hr' || user?.intern_role === 'hr';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/hr/resources');
      setResources(res.data.resources || []);
      setCategories(res.data.categories || []);
    } catch (err) {
      toast.error('Could not load resources');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const doDownload = async (r) => {
    try {
      const res = await api.get(`/hr/resources/${r.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: r.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = r.original_filename || r.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Local optimistic count bump
      setResources((prev) => prev.map((x) => x.id === r.id ? { ...x, downloads: (x.downloads || 0) + 1 } : x));
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const doUpload = async () => {
    if (!form.file) { toast.error('Pick a file to upload'); return; }
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('title', form.title);
      fd.append('description', form.description || '');
      fd.append('category', form.category || 'General');
      fd.append('tags', form.tags || '');
      fd.append('is_pinned', form.is_pinned ? 'true' : 'false');
      await api.post('/hr/resources', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Resource uploaded');
      setUploadOpen(false);
      setForm({ title: '', description: '', category: 'General', tags: '', is_pinned: false, file: null });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/hr/resources/${r.id}`);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error('Could not delete');
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = resources.filter((r) =>
    (!filterCat || r.category === filterCat) &&
    (!q || (r.title || '').toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q) || (r.tags || []).some((t) => t.toLowerCase().includes(q)))
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3 flex-wrap">
          <Button variant="ghost" size="icon" onClick={() => navigate('/employee/center')} data-testid="er-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <Link to="/employee/center" className="inline-block hover:opacity-70 transition-opacity" data-testid="er-title-link">
              <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                <FolderOpen className="w-6 h-6 text-emerald-600" /> Resource Center
              </h1>
            </Link>
            <p className="text-sm text-slate-500">Shared templates, policy docs, brand kits and everything you need.</p>
          </div>
          {canManage && (
            <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
              <DialogTrigger asChild>
                <Button data-testid="er-upload-btn"><Upload className="w-4 h-4 mr-1" /> Upload</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Upload a new resource</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <Input placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} data-testid="er-form-title" />
                  <Textarea placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
                  <div className="flex gap-2">
                    <select className="flex-1 border rounded-md px-3 py-2" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
                      <option>General</option><option>Policies</option><option>Onboarding</option>
                      <option>Brand</option><option>Templates</option><option>Training</option>
                    </select>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={form.is_pinned} onChange={(e) => setForm({...form, is_pinned: e.target.checked})} />
                      Pin
                    </label>
                  </div>
                  <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({...form, tags: e.target.value})} />
                  <Input type="file" onChange={(e) => setForm({...form, file: e.target.files[0]})} data-testid="er-form-file" />
                  <Button className="w-full" onClick={doUpload} disabled={uploading} data-testid="er-form-submit">
                    {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
                    Upload
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-4 md:p-6 space-y-4">
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              className="pl-9 pr-3 py-2 rounded-md border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-400"
              placeholder="Search resources..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="er-search"
            />
          </div>
          <select className="border rounded-md px-3 py-2 text-sm" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="er-cat-filter">
            <option value="">All categories</option>
            {categories.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-slate-500">
            {resources.length === 0 ? 'No resources uploaded yet.' : 'No results.'}
          </CardContent></Card>
        ) : (
          <div className="grid gap-2" data-testid="er-list">
            {filtered.map((r) => (
              <Card key={r.id} className="border border-slate-200" data-testid={`er-item-${r.id}`}>
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center text-[10px] font-bold uppercase">
                    {r.is_pinned ? <Pin className="w-5 h-5" /> : (ICON_BY_EXT(r.original_filename)?.slice(0, 4) || <File className="w-5 h-5" />)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800 truncate">{r.title}</p>
                    {r.description && <p className="text-xs text-slate-500 line-clamp-1">{r.description}</p>}
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge className="bg-slate-100 text-slate-600 text-[10px]">{r.category}</Badge>
                      {(r.tags || []).map((t) => (
                        <span key={t} className="text-[10px] text-slate-400">#{t}</span>
                      ))}
                      <span className="text-[10px] text-slate-400">{bytesFmt(r.size)} · {r.downloads || 0} downloads</span>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => doDownload(r)} data-testid={`er-download-${r.id}`}>
                    <Download className="w-4 h-4 mr-1" /> Download
                  </Button>
                  {canManage && (
                    <Button variant="ghost" size="icon" onClick={() => doDelete(r)} data-testid={`er-delete-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default EmployeeResourcesPage;
